import { Fragment, useEffect, useState, type ReactElement } from "react";
import { fullName } from "odyssey-shared";
import { AlertMark } from "./log.tsx";
import type { StepView } from "./stepper.ts";
import { closedSentence, optionDescription, throttledLine, waitSeconds } from "./words.ts";
import { say } from "../../words.ts";

/**
 * The chat panel's note line (ADR-068 decision 8 and its amendment "chat commands, Discord style"): the seconds the
 * account's messages still wait, a reserved command's answer, or the hint for the command being typed.
 */

/**
 * The seconds left of a wait, read four times a second while the wait lasts; React keeps a state set to the
 * value it holds, so a countdown changes as each second passes and never otherwise.
 *
 * @param until When the wait ends, on the page's clock, or 0 for none.
 * @param now The page's clock.
 * @returns The whole seconds left, or 0 once there is no wait.
 */
export function useSecondsLeft(until: number, now: () => number): number {
  /**
   * The whole seconds left now.
   *
   * @returns The seconds, or 0 once the wait is over.
   */
  const left = (): number => (until > now() ? waitSeconds(until - now()) : 0);
  const [seconds, setSeconds] = useState(left);
  useEffect(() => {
    setSeconds(left());
    if (until <= now()) {
      return undefined;
    }
    const timer = setInterval(() => {
      const next = left();
      setSeconds(next);
      if (next === 0) {
        clearInterval(timer);
      }
    }, 250);
    return () => clearInterval(timer);
  }, [until]);
  return seconds;
}

/**
 * The note line's words: the account's wait first, then a reserved command's answer, both errors with the alert
 * mark; otherwise, while the input has focus, the hint for the command being typed.
 *
 * @param props The `seconds` the account's messages still wait, the draft's `view`, and whether the input is
 *   `focused`.
 * @returns The note's content, or null for none.
 */
export function Note({ seconds, view, focused }: { readonly seconds: number; readonly view: StepView; readonly focused: boolean }): ReactElement | null {
  if (seconds > 0) {
    return (
      <span className="ot-chat__note-error" data-chat="note-error">
        <AlertMark />
        {throttledLine(seconds)}
      </span>
    );
  }
  if (!focused) {
    return null;
  }
  if (view.reserved !== null) {
    return (
      <span className="ot-chat__note-error" data-chat="note-error">
        <AlertMark />
        {closedSentence(view.reserved)}
      </span>
    );
  }
  const command = view.command;
  if (command === null || command.options.length === 0) {
    return null;
  }
  const reply = command.id === "reply" ? (view.replyTo === null ? say("chat.replyToNobody") : say("chat.replyTo", { name: fullName(view.replyTo.firstName, view.replyTo.familyName) })) : null;
  const description = reply ?? optionDescription(command, view.option);
  return (
    <span className="ot-chat__hint" data-chat="hint">
      <span className="ot-chat__hint-syntax">/{command.words[0]}</span>
      {command.options.map((option, index) => (
        <Fragment key={option.name}>
          {" "}
          <span className={index === view.option ? "ot-chat__hint-option ot-chat__hint-option--current" : "ot-chat__hint-option"} data-chat-option={index === view.option ? "current" : undefined}>
            {option.optional ? `[${option.name}]` : option.name}
          </span>
        </Fragment>
      ))}
      {description === "" ? null : <span className="ot-chat__hint-description">{description}</span>}
    </span>
  );
}

/**
 * Whether the note line has words to show, as `Note` decides them.
 *
 * @param seconds The seconds the account's messages still wait.
 * @param view The draft's view.
 * @param focused Whether the input has focus.
 * @returns True when `Note` draws something.
 */
export function noteShows(seconds: number, view: StepView, focused: boolean): boolean {
  if (seconds > 0) {
    return true;
  }
  return focused && (view.reserved !== null || (view.command !== null && view.command.options.length > 0));
}
